import type { BulkUploadBatchSummary } from '@/types'
import type { BulkUploadKpiItem } from './BulkUploadKpiGrid'

/** Shared outcome breakdown — same labels for KPI cards and filter chips. */
export const BULK_UPLOAD_OUTCOME_BREAKDOWN = [
  { outcomeKey: '', labelKey: 'outcomes.allFiles', fallback: 'All files in batch' },
  { outcomeKey: 'inserted_from_pending', labelKey: 'outcomes.inserted_from_pending', fallback: 'Approved from pending' },
  { outcomeKey: 'inserted_new', labelKey: 'outcomes.inserted_new', fallback: 'New approval inserted' },
  { outcomeKey: 'skipped_already_approved', labelKey: 'outcomes.skipped_already_approved', fallback: 'Skipped (already approved)' },
  { outcomeKey: 'failed_s3', labelKey: 'outcomes.failed_s3', fallback: 'S3 upload failed' },
  { outcomeKey: 'failed_sap', labelKey: 'outcomes.failed_sap', fallback: 'SAP / document error' },
  { outcomeKey: 'failed_rate_limit', labelKey: 'outcomes.failed_rate_limit', fallback: 'Rate limited (429)' },
  { outcomeKey: 'failed_retry_exhausted', labelKey: 'outcomes.failed_retry_exhausted', fallback: 'Failed (max retries)' },
  { outcomeKey: 'rolled_back', labelKey: 'outcomes.rolled_back', fallback: 'Rolled back' },
  { outcomeKey: 'processing', labelKey: 'outcomes.processing', fallback: 'Still processing' },
  { outcomeKey: 'pending', labelKey: 'outcomes.pending', fallback: 'Awaiting processing' },
] as const

export function countForOutcomeBreakdown(
  summary: BulkUploadBatchSummary | null | undefined,
  outcomeKey: string
): number {
  if (!summary) return 0
  if (!outcomeKey) return summary.totalFiles
  if (outcomeKey === 'inserted_from_pending') return summary.approvedUpdated
  if (outcomeKey === 'inserted_new') return summary.approvedInserted
  if (outcomeKey === 'skipped_already_approved') return summary.skippedApproved
  return summary.outcomes?.[outcomeKey] ?? 0
}

export function buildBatchKpiItems(summary: BulkUploadBatchSummary): BulkUploadKpiItem[] {
  return [
    { key: 'total', labelKey: 'stats.totalFiles', fallback: 'Files in batch', value: summary.totalFiles, tone: 'default' },
    {
      key: 'fromPending',
      labelKey: 'outcomes.inserted_from_pending',
      fallback: 'Approved from pending',
      value: summary.approvedUpdated,
      tone: 'blue',
    },
    {
      key: 'inserted',
      labelKey: 'outcomes.inserted_new',
      fallback: 'New approval inserted',
      value: summary.approvedInserted,
      tone: 'emerald',
    },
    {
      key: 'skipped',
      labelKey: 'outcomes.skipped_already_approved',
      fallback: 'Skipped (already approved)',
      value: summary.skippedApproved,
      tone: 'amber',
    },
    {
      key: 'attachments',
      labelKey: 'stats.attachments',
      fallback: 'Extra attachments',
      value: summary.attachmentsCreated,
      tone: 'purple',
    },
    { key: 'failed', labelKey: 'stats.failedTotal', fallback: 'Failed (all errors)', value: summary.failed, tone: 'red' },
  ]
}
